import React from 'react';
import { getContext } from 'common';
import _merge from 'lodash/merge';

import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

import Styled from './styles';

export interface ElevationProps extends React.ComponentPropsWithRef<'div'> {
  /** The elevation number that defines the style of the container. */
  elevationIndex?: 0 | 1 | 2 | 3 | 4 | 6 | 8 | 9 | 12 | 16 | 24;
  /** Set `true`, to render the darker background color without changing the theme. */
  dark?: boolean;
  /** Set to `true` to set a transparent background color. This property overrides the dark property setting.
   *  Useful for nesting Elevations. */
  transparent?: boolean;
  /** The content of the Elevation. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** If true, allow the override of the background elevation color. */
  allowElevationBackgroundColorOverride?: boolean;
  /**
   * To internally override the RTL
   * @ignore
   */
  rtl?: boolean;
  /** @ignore */
  style?: React.CSSProperties
}

const getElevationStyle = ({ theme: themeProp, elevationIndex = 0, dark, transparent }: Partial<ElevationProps>) => getContext(({ theme: { ELEVATIONS: theme } }) => {
  const resolvedTheme = _merge({}, theme, themeProp);
  const {
    lightBackgroundColor,
    darkBackgroundColor,
    shadowColorOne,
    shadowColorTwo,
  } = resolvedTheme[elevationIndex];
  const spreadingValue = elevationIndex === 0 ? '0' : Tokens.SHADOW_SPREAD[`SHADOW_SPREAD_${elevationIndex}`];
  return ({
    boxShadow: `0 0 ${spreadingValue} 0 ${shadowColorOne as string}, 0 ${spreadingValue} ${spreadingValue} 0 ${shadowColorTwo as string}`,
    backgroundColor: transparent ? 'transparent' : dark ? darkBackgroundColor : lightBackgroundColor,
  });
});

/** All props will be provided to the root element. To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference. */
const Elevation = React.forwardRef<HTMLElement, ElevationProps>((props: ElevationProps, ref) => getContext(({ theme: { ELEVATIONS: theme } }) => {
  const {
    elevationIndex,
    children,
    dark,
    theme: themeProp,
    transparent,
    allowElevationBackgroundColorOverride,
    style,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme, themeProp);

  const {
    lightBackgroundColor,
    darkBackgroundColor,
    shadowColorOne,
    shadowColorTwo,
  } = resolvedTheme[elevationIndex];
  const resolvedBackground = allowElevationBackgroundColorOverride ? undefined : dark ? darkBackgroundColor : lightBackgroundColor;
  const elevationProps = {
    shadowColorOne,
    shadowColorTwo,
    backgroundColor: transparent ? 'transparent' : resolvedBackground,
    spreadingValue: elevationIndex === 0 ? '0' : Tokens.SHADOW_SPREAD[`SHADOW_SPREAD_${elevationIndex}`],
    ...otherProps,
    ref
  };

  return (
    <Styled.Elevation {...elevationProps} style={style}>
      {children}
    </Styled.Elevation>
  );
}));

Elevation.displayName = 'Elevation';

Elevation.defaultProps = {
  style: {},
  dark: false,
  elevationIndex: 0,
  transparent: false,
  allowElevationBackgroundColorOverride: false,
  theme: {},
  rtl: false,
};

export { Elevation as default, getElevationStyle };
